import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  TimelineTimeframe,
  UnassignedShift,
  UnassignedShiftStackByDay,
} from '@wilson/interfaces';
import { toNumberOfShiftPerDayRecordKey } from '@wilson/non-domain-specific/shift-helpers/pipes';
import { addDays, eachDayOfInterval, subDays } from 'date-fns';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { UnassignedDateToShiftRecords } from '../interfaces';
import { ShiftTimelineDataState } from '../shift-timeline-data/shift-timeline-data.state';
import { ShiftTimelineFilterUnassignedRegionState } from '../shift-timeline-filter-unassigned-region/shift-timeline-filter-unassigned-region.state';
import { ShiftTimelineUnassignedRegionState } from './shift-timeline-unassigned-region.state';

@Injectable({
  providedIn: 'root',
})
export class ShiftTimelineUnassignedRegionStateFacadeService {
  private readonly store = inject(Store);

  readonly unassignedShiftsWithoutActivities$: Observable<UnassignedShift[]> =
    combineLatest([
      this.store.select(
        ShiftTimelineUnassignedRegionState.unassignedShiftsWithoutActivities,
      ),
      this.store.select(ShiftTimelineDataState.visibleDateRange),
    ]).pipe(
      map(([shifts, timeFrame]) => {
        if (!timeFrame.start || !timeFrame.end) {
          return [];
        } else {
          const timeFrameStart = new Date(timeFrame.start).getTime();
          const timeFrameEnd = new Date(timeFrame.end).getTime();

          return Object.values(shifts).filter((shift) => {
            const shiftStart = new Date(shift.startDatetime).getTime();
            const shiftEnd = new Date(shift.endDatetime).getTime();
            return shiftStart <= timeFrameEnd && shiftEnd >= timeFrameStart;
          });
        }
      }),
      shareReplay(1),
    );

  readonly unassignedShiftsWithoutActivitiesOverlapLevels$ = combineLatest([
    this.store.select(
      ShiftTimelineUnassignedRegionState.unassignedDateToShiftRecords,
    ),
    this.store.select(ShiftTimelineDataState.visibleDateRange),
  ]).pipe(
    map(
      ([unassignedDateToShiftRecords, visibleDateRange]: [
        UnassignedDateToShiftRecords,
        TimelineTimeframe,
      ]) => {
        if (!visibleDateRange.start || !visibleDateRange.end) {
          return [] as UnassignedShiftStackByDay[];
        } else {
          const adjustedVisibleDateRange = {
            start: subDays(visibleDateRange.start, 1),
            end: addDays(visibleDateRange.end, 1),
          };

          return eachDayOfInterval(adjustedVisibleDateRange).map((day) => {
            return {
              day,
              shifts:
                unassignedDateToShiftRecords[
                  toNumberOfShiftPerDayRecordKey(day)
                ] || {},
            };
          }) as UnassignedShiftStackByDay[];
        }
      },
    ),
    shareReplay(1),
  );

  readonly filteredUnassignedShiftsStacksWithoutActivities$ = combineLatest([
    this.unassignedShiftsWithoutActivitiesOverlapLevels$,
    this.store.select(
      ShiftTimelineFilterUnassignedRegionState.unassignedShiftIds,
    ),
    this.store.select(
      ShiftTimelineFilterUnassignedRegionState.unassignedShiftSearches,
    ),
  ]).pipe(
    map(
      ([
        unassignedShiftsWithoutActivitiesOverlapLevels,
        unassignedShiftIds,
        unassignedShiftSearches,
      ]) => {
        return ShiftTimelineFilterUnassignedRegionState.filterUnassignedShiftStacks(
          unassignedShiftsWithoutActivitiesOverlapLevels,
          unassignedShiftIds,
          unassignedShiftSearches,
        );
      },
    ),
    shareReplay(1),
  );
}
